import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { Subscription } from 'rxjs';

import { MyUtils } from 'src/app/services/my-utils.service';

import {
  ChatbotService,
  ChatHistoryMessage,
  ChatSessionResponse,
} from 'src/app/services/chatbot.service';

interface ChatMessage {
  text: string;
  html?: SafeHtml;
  sender: 'user' | 'bot';
  timestamp: Date;
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
})
export class ChatbotComponent implements OnDestroy {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;

  isOpen = false;
  userInput = '';
  isTyping = false;
  isSessionLoading = false;
  mcpSessionId: string | null = null;
  panelWidth = 380;
  panelHeight = 520;

  messages: ChatMessage[] = [];

  private sessionSub?: Subscription;
  private chatSub?: Subscription;
  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;

  constructor(
    public utils: MyUtils,
    private chatbotService: ChatbotService,
    private sanitizer: DomSanitizer
  ) {
    marked.setOptions({ breaks: true, gfm: true });
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      if (!this.mcpSessionId && !this.isSessionLoading) {
        this.initSession();
      }
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  startResize(event: PointerEvent): void {
    if (window.innerWidth <= 480) {
      return;
    }

    event.preventDefault();
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.panelWidth;
    this.resizeStartHeight = this.panelHeight;
  }

  @HostListener('document:pointermove', ['$event'])
  onResize(event: PointerEvent): void {
    if (!this.isResizing) {
      return;
    }

    const maxWidth = Math.max(320, window.innerWidth - 48);
    const maxHeight = Math.max(360, window.innerHeight - 120);

    this.panelWidth = Math.min(
      maxWidth,
      Math.max(320, this.resizeStartWidth + this.resizeStartX - event.clientX)
    );
    this.panelHeight = Math.min(
      maxHeight,
      Math.max(360, this.resizeStartHeight + this.resizeStartY - event.clientY)
    );
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  stopResize(): void {
    this.isResizing = false;
  }

  sendMessage(): void {
    const text = this.userInput.trim();
    if (!text || this.isTyping || this.isSessionLoading) {
      return;
    }

    if (!this.mcpSessionId) {
      this.pushBotMessage('Chat session is not ready yet. Please wait a moment and try again.');
      return;
    }

    this.messages.push({
      text,
      sender: 'user',
      timestamp: new Date(),
    });
    this.userInput = '';
    this.scrollToBottom();

    this.isTyping = true;
    this.chatSub?.unsubscribe();
    this.chatSub = this.chatbotService
      .sendMessage(
        this.mcpSessionId,
        this.messages.map((msg) => ({ text: msg.text, sender: msg.sender }))
      )
      .subscribe({
        next: (response) => {
          this.syncMcpSessionIdFromResponse(response);
          this.isTyping = false;

          const text = this.chatbotService.extractBotText(response);
          if (!text) {
            this.pushBotMessage('Sorry, I could not process your request.');
            return;
          }

          this.pushBotMessage(text);
        },
        error: () => {
          this.isTyping = false;
          this.pushBotMessage('Something went wrong. Please try again.');
        },
      });
  }

  ngOnDestroy(): void {
    this.sessionSub?.unsubscribe();
    this.chatSub?.unsubscribe();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private initSession(): void {
    if (this.mcpSessionId || this.isSessionLoading) {
      return;
    }

    this.isSessionLoading = true;
    this.userInput = '';
    this.isTyping = false;

    if (this.messages.length === 0) {
      this.pushBotMessage('Hello! How can I help you today?');
    }

    this.sessionSub?.unsubscribe();
    this.sessionSub = this.chatbotService
      .createSession(this.chatbotService.buildSessionPayload())
      .subscribe({
        next: (response) => {
          const mcpSessionId = this.chatbotService.extractMcpSessionId(response);
          if (!mcpSessionId) {
            this.handleSessionError();
            return;
          }

          this.mcpSessionId = mcpSessionId;
          this.chatbotService.storeMcpSessionId(mcpSessionId);

          const hasServerWelcome =
            (Array.isArray(response.messages) && response.messages.length > 0) ||
            !!this.chatbotService.extractBotText(response);

          if (hasServerWelcome) {
            this.messages = this.mapSessionMessages(response);
          }

          this.isSessionLoading = false;
          this.scrollToBottom();
        },
        error: () => {
          this.handleSessionError();
        },
      });
  }

  private handleSessionError(): void {
    this.isSessionLoading = false;
    this.mcpSessionId = null;
    this.messages = [];
    this.pushBotMessage('Unable to start chat. Please try again.');
  }

  private mapSessionMessages(response: ChatSessionResponse): ChatMessage[] {
    if (Array.isArray(response.messages) && response.messages.length > 0) {
      return response.messages
        .map((message) => this.mapHistoryMessage(message))
        .filter((message): message is ChatMessage => message !== null);
    }

    const welcomeText = this.chatbotService.extractBotText(response);
    if (welcomeText) {
      return [this.createBotMessage(welcomeText)];
    }

    return [this.createBotMessage('Hello! How can I help you today?')];
  }

  private mapHistoryMessage(message: ChatHistoryMessage): ChatMessage | null {
    const text = message.text ?? message.content ?? message.message;
    if (!text) {
      return null;
    }

    const role = (message.sender ?? message.role ?? '').toLowerCase();
    const sender: 'user' | 'bot' =
      role === 'user' || role === 'human' ? 'user' : 'bot';

    if (sender === 'bot') {
      return this.createBotMessage(text);
    }

    return {
      text,
      sender,
      timestamp: new Date(),
    };
  }

  private syncMcpSessionIdFromResponse(
    response: { mcpSessionId?: string; data?: { mcpSessionId?: string } }
  ): void {
    const mcpSessionId = this.chatbotService.extractMcpSessionId(response);
    if (!mcpSessionId || mcpSessionId === this.mcpSessionId) {
      return;
    }

    this.mcpSessionId = mcpSessionId;
    this.chatbotService.storeMcpSessionId(mcpSessionId);
  }

  private pushBotMessage(text: string): void {
    this.messages.push(this.createBotMessage(text));
    this.scrollToBottom();
  }

  private createBotMessage(text: string): ChatMessage {
    return {
      text,
      html: this.renderMarkdown(text),
      sender: 'bot',
      timestamp: new Date(),
    };
  }

  private renderMarkdown(text: string): SafeHtml {
    const html = marked.parse(text ?? '', { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
